import { is, range, path } from 'ramda'
import moment from 'moment'
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'

import axios from '@/api/axios.js'

import { useDevConsole } from '@/composables/dev-console'
import {
  ENDPOINTS,
  DATE_FORMAT, MODALS,
} from '@/lib/consts.ts'
import { isRepeatingAppointment } from '@/components/appointment/functions.js'

import { useCalendarService } from '@/components/calendar/composables/calendar-service/index.js'
import { useEmitter } from '@/composables/emitter'
import { useAnalytics } from '@/composables/analytics'
import { useAuthentication, LOGOUT_REASONS } from '@/composables/authentication'
import {
  useDatadogLoginActions,
  DATADOG_LOGIN_METHODS,
  DATADOG_LOGIN_ENDPOINTS,
} from '@/components/onboarding/composables/datadog-login-actions/index.js'
import { useCore } from '@/composables/core'
import { modalOpen } from '@/plugins/event-bus'
import { useLogger } from '@booksy-com/vue-tools'

declare module 'axios' {
  export interface AxiosRequestConfig {
    syncInterceptors?: boolean
  }
}

type HttpMethod = NonNullable<AxiosRequestConfig['method']>

interface RequestCarrier {
  config?: AxiosRequestConfig
}

type ApiValidationError = {
  field?: string
  code?: string
  description?: string
}

export function registerInterceptors (): void {
  const logger = useLogger('interceptor')
  logger.debug('registering axios interceptors')

  const emitter = useEmitter()
  const analytics = useAnalytics()

  const { store, t } = useCore()

  /*
   *
   * log request
   *
   */
  axios.interceptors.request.use((config) => {
    useDevConsole().logRequest({
      method: String(config.method ?? ''),
      url: String(config.url ?? ''),
      params: (config.params as Record<string, unknown>) ?? {},
    })
    return config
  })

  /*
   *
   * reset calendar after appointment create...
   *
   */
  axios.interceptors.response.use(
    async (response: AxiosResponse) => {
      const { store } = useCore()

      const sync = response.config.syncInterceptors

      /*
       * clear calendar service after appointment create
       */
      if (isReq(response, ENDPOINTS.APPOINTMENT_UID, ['post', 'put'])) {
        const data = JSON.parse(response.config.data as string) as { dry_run?: boolean; booked_from: string }
        if (!data.dry_run) {
          logger.debug('appointment created or updated; clearing calendar service...')
          await setInvalidCalendarDates([data.booked_from])
        }
      }

      if (isReq(response, ENDPOINTS.APPOINTMENT_ACTION, ['post'])) {
        const { data } = response
        logger.debug('appointment action made; clearing calendar service...')
        await setInvalidCalendarDates([data.appointment.booked_from])
      }

      if (isReq(response, ENDPOINTS.APPOINTMENT_DRAG, ['put'])) {
        const { data } = response
        if (data?.bookings?.length && isRepeatingAppointment(data.bookings[0])) {
          logger.debug('repeating appointment drag&drop; clearing calendar service...')
          await setInvalidCalendarDatesWithoutCurrent(data.bookings[0].booked_from)
        }
      }

      if (
        isReq(response, ENDPOINTS.RESERVATION, ['post', 'put'])
        || isReq(response, ENDPOINTS.RESERVATION_NEW, ['post'])
      ) {
        const data = JSON.parse(response.config.data as string) as { reserved_from: string }
        logger.debug('reservation created or updated; clearing calendar service...')
        await setInvalidCalendarDates([data.reserved_from])
      }

      if (isReq(response, ENDPOINTS.SHIFTS, ['post', 'put'])) {
        const data = JSON.parse(response.config.data as string) as {
          start_date: string
          end_date: string
        }
        const dates = data.start_date === data.end_date
          ? [data.start_date]
          : getDatesRange(data.start_date, data.end_date)

        logger.debug('time off created or updated; clearing calendar service dates: ', dates)
        await setInvalidCalendarDates(dates)
      }

      // @ts-ignore - custom flag set by callers; not in AxiosRequestConfig type
      const skipServiceCategoriesReload = response.config.skipServiceCategoriesReload

      if (
        !skipServiceCategoriesReload
        && (
          isReq(response, ENDPOINTS.SERVICES, ['post', 'put'])
          || isReq(response, ENDPOINTS.SERVICE, ['post', 'put', 'delete'])
          || isReq(response, ENDPOINTS.SERVICE_CATEGORIES, ['post', 'put'])
          || isReq(response, ENDPOINTS.SERVICE_CATEGORY, ['post', 'put', 'delete'])
          || isReq(response, ENDPOINTS.BUSINESS, ['post', 'put'])
          || isReq(response, ENDPOINTS.POS_APPLY_TAX_RATE_FOR_SERVICES, ['post'])
          || isReq(response, ENDPOINTS.POS_APPLY_TAX_RATE_FOR_PRODUCTS, ['post'])
          || isReq(response, ENDPOINTS.NO_SHOW_PROTECTION, ['post', 'put'])
        )
      ) {
        await store.dispatch('business/loadServiceCategories')
      }

      if (
        isReq(response, ENDPOINTS.RESOURCES, ['post', 'put'])
        || isReq(response, ENDPOINTS.RESOURCE_ID, ['put', 'delete'])
      ) {
        store.dispatch('business/reloadBusiness')
      }

      const updateBusiness = (data: unknown) => store.dispatch('business/updateBusiness', data)

      if (isReq(response, ENDPOINTS.BUSINESS, ['post', 'put']) && response?.data) {
        sync
          ? await updateBusiness(response.data)
          : updateBusiness(response.data)

        analytics.dispatch(emitter.EVENTS.BUSINESS_INFO_UPDATED, {
          business: response.data.business,
          country: store.state?.app?.country,
          from_server: true,
        })

        if (response.data.categories_changed) {
          analytics.dispatch(emitter.EVENTS.BUSINESS_CATEGORIES_UPDATED, {
            business: response.data.business,
          })
        }
      }

      if (
        (
          isReq(response, ENDPOINTS.POS_TRANSACTIONS, ['post', 'put'])
          || isReq(response, ENDPOINTS.POS_TRANSACTION, ['post', 'put'])
        )
        && response.data.register
      ) {
        store.commit('cashRegister/setRegister', response.data.register)
      }

      if (
        isReq(response, ENDPOINTS.POS_APPLY_TAX_RATE_FOR_SERVICES, ['post'])
        || isReq(response, ENDPOINTS.POS_APPLY_TAX_RATE_FOR_PRODUCTS, ['post'])
      ) {
        await store.dispatch('business/loadServiceCategories')
      }

      return response
    },
    async (error: AxiosError) => {
      if (isReq(error, ENDPOINTS.ACCOUNT_EXISTS, ['get'])) {
        const { startLoginProcess, reportLoginAction } = useDatadogLoginActions()
        startLoginProcess(DATADOG_LOGIN_METHODS.EMAIL)
        reportLoginAction({
          isWorkingProperly: false,
          response: error,
          info: DATADOG_LOGIN_ENDPOINTS.ACCOUNT_EXISTS,
        })
      }

      const accessToken = path(['identity', 'accessToken'], store.state)
      const errorList = path(['response', 'data', 'errors'], error) as ApiValidationError[] | undefined
      if (Array.isArray(errorList)) {
        const accessTokenError = errorList.find(item => item && item.field === 'access_token')
        const twoFAError = errorList.find(item => item && item.field === 'otp_code')
        // Access token expired (or env. missmatch).
        // Check if has access token to prevent from multiple logout calls at
        // the same time.
        if (accessTokenError && accessToken) {
          emitter.emit(emitter.EVENTS.API_ACCESS_TOKEN_ERROR)

          analytics.dispatch(emitter.EVENTS.ACCESS_TOKEN_ERROR, {
            accessToken,
          })

          // Logout user
          useAuthentication().logout(true, {
            reason: LOGOUT_REASONS.EXPIRED_SESSION,
          })
        }

        // handle 2FA journey
        if (twoFAError) {
          const errors = errorList.filter(item => item.field === 'otp_code')

          return modalOpen(MODALS.OTP_MODAL, {
            errors,
          }).then(async (code) => {
            try {
              const cfg = error.config
              if (!cfg) {
                return Promise.reject(error)
              }
              const response = await axios({
                ...cfg,
                headers: { ...cfg.headers, 'x-otp-code': code },
              })
              return Promise.resolve(response)
            } catch (retryError) {
              return Promise.reject(retryError)
            }
          }).catch((modalError: unknown) => {
            if (typeof modalError === 'string' || !(modalError as AxiosError).response) {
              if (error.response?.data && typeof error.response.data === 'object') {
                const data = error.response.data as { errors?: ApiValidationError[] }
                data.errors = [{
                  code: 'otp_required',
                  description: t('components.payments.two-factor-authentication.error'),
                }]
              }
              return Promise.reject(error)
            }
            return Promise.reject(modalError)
          })
        }
      }

      return Promise.reject(error)
    }
  )
}

function isReq (
  response: RequestCarrier,
  endpoint: string,
  methods: HttpMethod | readonly HttpMethod[] | '*' = '*'
): boolean {
  const { config } = response
  if (!config) {
    return false
  }
  if (methods !== '*') {
    const m = (is(Array, methods) ? methods : [methods]) as HttpMethod[]
    if (!m.includes(config.method as HttpMethod)) {
      return false
    }
  }
  // convert endpoint into regular expression
  const re = new RegExp(endpoint.replace(/:[^/]+/g, '[^/]+') + '$', 'g')
  return re.test(config.url ?? '')
}

function setInvalidCalendarDates (dates: string[]): Promise<void> {
  const calendarService = useCalendarService()
  calendarService?.setInvalidDates(dates)

  return Promise.resolve()
}

function setInvalidCalendarDatesWithoutCurrent (date: string): Promise<void> {
  const calendarService = useCalendarService()
  calendarService?.setInvalidDatesWithoutCurrent(date)

  return Promise.resolve()
}

function getDatesRange (_from: string, _to: string): string[] {
  const from = moment(_from)
  const to = moment(_to)
  const dates = to.diff(from, 'days') + 1
  return range(0, dates).map(i => from.clone().add(i, 'day').format(DATE_FORMAT))
}
